import {
  capitalizeFirstLetter,
  formatCurrency,
  formatDate,
  DateFormat,
} from "../../../../../utils";
import {
  LiabilitiesDetails,
  LiabilityInterestRateHistory,
} from "../../../types";
import { BreakdownStat } from "./types";

export const getRepaidPercent = (liability: LiabilitiesDetails): number => {
  const amountLent = liability.amountLent ?? 0;
  if (amountLent <= 0) {
    return 0;
  }
  const principalRepaid = amountLent - liability.currentAmount;
  return Math.min(100, Math.max(0, (principalRepaid / amountLent) * 100));
};

export const buildBreakdownStats = (
  liability: LiabilitiesDetails,
): BreakdownStat[] => {
  const amountLent = liability.amountLent ?? 0;
  const stillOwed = liability.currentAmount;
  const principalRepaid = amountLent - stillOwed;

  return [
    {
      key: "repaid",
      label: "Principal repaid",
      shortLabel: "Principal repaid",
      value: formatCurrency(principalRepaid),
      swatch: "repaid",
      tone: "primary",
    },
    {
      key: "owed",
      label: "Still owed",
      shortLabel: "Still owed",
      value: formatCurrency(stillOwed),
      swatch: "owed",
      tone: "default",
    },
    {
      key: "interest",
      label: "Interest paid so far",
      shortLabel: "Interest paid",
      value: formatCurrency(liability.interestAmount),
      swatch: "interest",
      tone: "error",
    },
    {
      key: "paid",
      label: "Total paid",
      shortLabel: "Total paid",
      value: formatCurrency(liability.paidAmount),
      swatch: "paid",
      tone: "default",
    },
    {
      key: "borrowed",
      label: "Borrowed",
      shortLabel: "Borrowed",
      value: formatCurrency(amountLent),
      swatch: "borrowed",
      tone: "muted",
    },
  ];
};

export const getRateSubLabel = (
  latestRate: LiabilityInterestRateHistory | undefined,
): string | undefined => {
  if (!latestRate) {
    return undefined;
  }
  const rateType = capitalizeFirstLetter(latestRate.rateType);
  if (!latestRate.effectiveDate) {
    return rateType;
  }
  return `${rateType} · since ${formatDate(latestRate.effectiveDate, DateFormat.DMY_SPACE)}`;
};
